'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { ImQuotesRight } from 'react-icons/im';
import { CurvedDivider } from './CurvedDivider';
import BackgroundBeams from './ui/background-beams';
import { Content, asHTML, asText } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import DragIndicator from './DragIndicator';

interface ReviewsListProps {
  list: Content.QuotesDocument[];
  reverse?: boolean;
  duration?: number;
}

interface ReviewsCarouselProps {
  data: Content.ReviewsCarouselDocument['data'];
  quotes: Content.QuotesDocument[];
}

const ReviewsCarousel: React.FC<ReviewsCarouselProps> = ({ data, quotes }) => {
  // Split quotes into three groups for the three rows
  const splitQuotes = (
    quotes: Content.QuotesDocument[]
  ): [Content.QuotesDocument[], Content.QuotesDocument[], Content.QuotesDocument[]] => {
    const third = Math.ceil(quotes.length / 3);
    return [quotes.slice(0, third), quotes.slice(third, 2 * third), quotes.slice(2 * third)];
  };

  const [topQuotes, middleQuotes, bottomQuotes] = splitQuotes(quotes);

  return (
    <section
      id="reviews"
      className="scrolling-testimonials relative overflow-x-hidden bg-gradient-to-l from-blue-950 to-blue-800 py-16 sm:py-24"
    >
      <BackgroundBeams opacity={0.05} />
      <div className="fade-up container mx-auto mb-2 h-full sm:mb-6">
        <h3 className="mx-auto max-w-2xl text-center text-3xl font-light leading-tight text-white sm:text-center sm:text-4xl sm:font-thin xl:text-4xl">
          {data.title}
        </h3>
        <p className="mt-4 w-full text-center text-base text-blue-300 sm:text-center">
          {data.description}
        </p>
      </div>
      <div className="relative py-10">
        <div className="absolute bottom-0 left-0 top-0 z-10 w-4 bg-gradient-to-r from-blue-800 to-transparent opacity-50 sm:w-24 sm:opacity-80" />

        <div className="relative overflow-y-hidden">
          <div className="fade-up mb-4 flex items-center">
            <ReviewList list={topQuotes} duration={125} />
            <ReviewList list={topQuotes} duration={125} />
            <ReviewList list={topQuotes} duration={125} />
          </div>
          <div className="fade-up mb-4 flex items-center">
            <ReviewList list={middleQuotes} duration={75} reverse />
            <ReviewList list={middleQuotes} duration={75} reverse />
            <ReviewList list={middleQuotes} duration={75} reverse />
          </div>
          <div className="fade-up flex items-center">
            <ReviewList list={bottomQuotes} duration={275} />
            <ReviewList list={bottomQuotes} duration={275} />
            <ReviewList list={bottomQuotes} duration={275} />
          </div>
        </div>

        <div className="absolute bottom-0 right-0 top-0 z-10 w-4 bg-gradient-to-l from-blue-950 to-transparent opacity-50 sm:w-24 sm:opacity-80" />
      </div>
      <DragIndicator />
      <CurvedDivider top color="#fff" />
    </section>
  );
};

const ReviewList: React.FC<ReviewsListProps> = ({ list, reverse = false, duration = 50 }) => {
  return (
    <motion.div
      initial={{ translateX: reverse ? '-100%' : '0%' }}
      animate={{ translateX: reverse ? '0%' : '-100%' }}
      transition={{ duration, repeat: Infinity, ease: 'linear' }}
      className="flex gap-4 px-2"
    >
      {list.map((quote) => {
        return (
          <div
            key={quote.id}
            className="relative grid w-[400px] shrink-0 grid-cols-[7rem,_1fr] overflow-hidden rounded-lg sm:w-[500px]"
          >
            <PrismicNextImage
              field={quote.data.photo}
              width={112}
              height={208}
              sizes="112px"
              className="h-44 w-full object-cover opacity-70 grayscale sm:h-52"
            />
            <div className="bg-blue-600 p-4 text-slate-50 backdrop-blur">
              <blockquote className="flex h-full flex-col justify-between">
                <span
                  className="home-quote block pr-6 text-sm italic leading-snug text-blue-200 sm:pr-10 sm:text-[15px]"
                  dangerouslySetInnerHTML={{ __html: asHTML(quote.data.short_quote) }}
                />
                <footer>
                  <div>
                    <span className="block pr-12 text-sm font-medium text-blue-300">
                      {asText(quote.data.name)}
                      <span className="hidden sm:inline-block">&nbsp;- {quote.data.position}</span>
                    </span>
                    <span className="mb-[.2px] block text-sm font-medium text-blue-300">
                      {quote.data.company}
                    </span>
                  </div>
                </footer>
              </blockquote>

              <span className="absolute right-3 top-2 text-2xl text-blue-500">
                <ImQuotesRight />
              </span>
            </div>
          </div>
        );
      })}
    </motion.div>
  );
};

export default ReviewsCarousel;
